import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/store";
import { Dots } from "../components/Bits";
import { useSeo } from "../components/Seo";

import { Download, Gift, Star, Check } from "lucide-react";

const PERKS = [[Download, "Materiály ke stažení kdykoliv ve svém účtu"], [Gift, "Materiály zdarma za odběr newsletteru"], [Star, "Hodnocení zakoupených materiálů"], [Check, "Faktury a dárkové poukazy na jednom místě"]];

function Shell({ title, sub, children }) {
  useSeo({ title, noindex: true });
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
      <div className="grid lg:grid-cols-[1fr_1.1fr] rounded-[40px] overflow-hidden border border-[#f6e3ec] shadow-[0_40px_80px_-40px_rgba(212,86,140,.35)] bg-white">
        <div className="relative hidden lg:flex flex-col justify-between bg-[var(--blush)] p-10 overflow-hidden">
          <Dots />
          <img src="/logo.png" alt="" className="relative w-64 floaty" />
          <div className="relative space-y-4 mt-8">
            <p className="font-display text-2xl">Co vám účet přinese</p>
            {PERKS.map(([I, t], i) => (
              <p key={t} className="flex items-center gap-3 text-sm font-semibold"><span className="w-10 h-10 rounded-full grid place-items-center bg-white shadow-sm"><I className="w-4 h-4" style={{ color: ["var(--pink)", "var(--teal)", "#d9a800", "#9b6fd0"][i] }} /></span>{t}</p>
            ))}
          </div>
        </div>
        <div className="p-7 sm:p-12">
          <p className="eyebrow mb-2">Materiály pro školky</p>
          <h1 className="text-4xl sm:text-5xl font-semibold">{title}</h1>
          {sub && <p className="text-[var(--muted)] mt-2">{sub}</p>}
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

function useSubmit(fn) {
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return [busy, run];
}

export function Login() {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, run] = useSubmit(async () => {
    const { data } = await api.post("/auth/login", f);
    signIn(data);
    toast.success("Vítejte zpět!");
    nav(sp.get("next") || "/ucet");
  });
  return (
    <Shell title="Přihlášení" sub="Vaše materiály na vás čekají.">
      <form onSubmit={run} className="space-y-3" data-testid="login-form">
        <input data-testid="login-email-input" type="email" required placeholder="E-mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="field" />
        <input data-testid="login-password-input" type="password" required placeholder="Heslo" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="field" />
        <button data-testid="login-submit-btn" disabled={busy} className="btn-pink w-full">{busy ? "Přihlašuji…" : "Přihlásit se"}</button>
      </form>
      <div className="flex justify-between text-sm mt-5">
        <Link to="/zapomenute-heslo" className="underline" data-testid="forgot-link">Zapomenuté heslo</Link>
        <Link to={`/registrace${sp.get("next") ? `?next=${sp.get("next")}` : ""}`} className="font-bold text-[var(--pink-dark)]" data-testid="register-link">Vytvořit účet</Link>
      </div>
    </Shell>
  );
}

export function Register() {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", password: "", newsletter: false, terms: false });
  const [busy, run] = useSubmit(async () => {
    if (!f.terms) throw new Error("Potvrďte prosím souhlas s podmínkami");
    const { data } = await api.post("/auth/register", f);
    signIn(data);
    if (f.newsletter) toast.success("Účet vytvořen a materiály zdarma odemčeny!");
    nav(sp.get("next") || "/ucet");
  });
  return (
    <Shell title="Registrace" sub="Zakoupené materiály budete mít vždy po ruce.">
      <form onSubmit={run} className="space-y-3" data-testid="register-form">
        <input data-testid="register-name-input" required placeholder="Jméno a příjmení" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="field" />
        <input data-testid="register-email-input" type="email" required placeholder="E-mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="field" />
        <input data-testid="register-password-input" type="password" required minLength={6} placeholder="Heslo (min. 6 znaků)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="field" />
        <label className="flex gap-2 text-sm"><input type="checkbox" checked={f.newsletter} onChange={(e) => setF({ ...f, newsletter: e.target.checked })} className="accent-[var(--pink)]" data-testid="register-newsletter-checkbox" /> Chci odebírat newsletter a získat materiály zdarma</label>
        <label className="flex gap-2 text-sm"><input type="checkbox" checked={f.terms} onChange={(e) => setF({ ...f, terms: e.target.checked })} className="accent-[var(--pink)]" data-testid="register-terms-checkbox" /> <span>Souhlasím s <Link to="/obchodni-podminky" className="underline">obchodními podmínkami</Link> a <Link to="/gdpr" className="underline">GDPR</Link></span></label>
        <button data-testid="register-submit-btn" disabled={busy} className="btn-pink w-full">{busy ? "Vytvářím…" : "Vytvořit účet"}</button>
      </form>
      <p className="text-sm mt-5">Už účet máte? <Link to="/prihlaseni" className="font-bold text-[var(--pink-dark)]">Přihlaste se</Link></p>
    </Shell>
  );
}

export function Forgot() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, run] = useSubmit(async () => {
    await api.post("/auth/forgot-password", { email });
    setSent(true);
  });
  return (
    <Shell title="Zapomenuté heslo" sub="Pošleme vám odkaz pro nastavení nového hesla.">
      {sent ? <p data-testid="forgot-sent" className="font-hand text-xl">Pokud účet existuje, odkaz už letí do vaší schránky.</p> : (
        <form onSubmit={run} className="space-y-3">
          <input data-testid="forgot-email-input" type="email" required placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} className="field" />
          <button data-testid="forgot-submit-btn" disabled={busy} className="btn-pink w-full">Odeslat odkaz</button>
        </form>
      )}
    </Shell>
  );
}

export function Reset() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [busy, run] = useSubmit(async () => {
    await api.post("/auth/reset-password", { token: sp.get("token"), password });
    toast.success("Heslo změněno, můžete se přihlásit.");
    nav("/prihlaseni");
  });
  return (
    <Shell title="Nové heslo">
      <form onSubmit={run} className="space-y-3">
        <input data-testid="reset-password-input" type="password" required minLength={6} placeholder="Nové heslo" value={password} onChange={(e) => setPassword(e.target.value)} className="field" />
        <button data-testid="reset-submit-btn" disabled={busy} className="btn-pink w-full">Uložit heslo</button>
      </form>
    </Shell>
  );
}
